/**
 * TmAssigneeHoursFieldset — hours-mode replacement for AssigneeFieldset.
 *
 * Used on any work order that tracks hours: one ticked "Require Hours",
 * or one whose sub-type is T&M (which forces that flag on). Instead of the
 * standard primary-pill + additional-pills pattern, this renders one
 * editable row per assigned employee with a paired hours input, plus a
 * running total — measured against the work order's hour bucket when one
 * has been entered, so an over-run is visible while it's still being typed
 * rather than after the job is billed.
 *
 * Wire-up:
 *   - The primary assignee is row 0. The remaining rows are the
 *     additional assignees (in the order they appear in the array).
 *   - Adding a row appends to additionalAssigneeIds.
 *   - Removing row 0 promotes the first additional assignee to primary.
 *     If no additional assignees exist, the primary is cleared.
 *   - Hours live in a Record<userId, string> so the input can be
 *     temporarily empty mid-typing without coercing to 0.
 *
 * The parent (JobForm) owns assigneeId / additionalAssigneeIds and the
 * hours map; this component is purely controlled.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../../services/api';
import { AsyncSearchableSelect, AsyncSearchableSelectOption } from '../AsyncSearchableSelect';
import { formatAssigneeLabel, formatRoleLabel } from '../../../utils/formatLabel';
import { addPeopleToCrew } from '../../../utils/quickAddUser';
import type { QuickAddedPersonRef, QuickAddOptions } from '../../../utils/quickAddUser';
import { QuickAddUserLink, useQuickAddFooterAction } from '../QuickAddUserLink';

/* Anyone the requester can see is assignable — mirrors AssigneeFieldset,
 * where the only eligibility rule is the requester's own company access
 * scope, not a role subset and not the WO's customer company. */

interface DropdownUser {
	id: string | number;
	first_name: string;
	last_name: string;
	role?: string;
	/** Subcontractor's business name — shown in the label. */
	subcontractor_company?: string | null;
	/** Open (not completed/cancelled) counts from with_workload=1. */
	open_work_orders_count?: number;
	open_inspections_count?: number;
}

/* Same label rule as AssigneeFieldset — one shared helper. */
const displayLabel = (u: DropdownUser): string => formatAssigneeLabel(u);

/** Role + open-job context line — mirrors AssigneeFieldset's sublabel. */
const contextSublabel = (u: DropdownUser): string | undefined => {
	const parts: string[] = [];
	if (u.role && u.role !== 'subcontractor') {
		parts.push(formatRoleLabel(u.role));
	}
	if (u.open_work_orders_count != null || u.open_inspections_count != null) {
		const open = (u.open_work_orders_count ?? 0) + (u.open_inspections_count ?? 0);
		parts.push(`${open} open job${open === 1 ? '' : 's'}`);
	}
	return parts.length > 0 ? parts.join(' · ') : undefined;
};

const userToOption = (u: DropdownUser): AsyncSearchableSelectOption => ({
	value: String(u.id),
	label: displayLabel(u),
	sublabel: contextSublabel(u),
});

/** Same rule JobForm's validator applies: an assignee "has hours" only
 *  once the field holds a positive number. Empty, 0 and junk all count as
 *  missing, so the row flagged here is the row the summary is counting. */
const hasHours = (value: string | undefined): boolean =>
	value != null && value !== '' && Number(value) > 0;

export interface TmAssigneeHoursFieldsetProps {
	/** Currently selected primary assignee user ID (empty string when none). */
	assigneeId: string;
	onAssigneeChange: (id: string) => void;
	/** Additional assignee IDs in display order. */
	additionalAssigneeIds: string[];
	onAdditionalAssigneesChange: (ids: string[]) => void;
	/** Per-user hour values keyed by user id (as string). */
	assigneeHours: Record<string, string>;
	onAssigneeHoursChange: (hours: Record<string, string>) => void;
	/** The work order's bucket of hours (work_orders.allocated_hours), when
	 *  one has been entered. Drives the "x / y hrs" readout and the
	 *  over-allocation warning below the rows. Undefined = no bucket set,
	 *  which is allowed on everything except T&M. */
	bucketHours?: number;
	/** Whether the T&M qualifier belongs on the heading. Off for a plain
	 *  "Require Hours" work order, which tracks hours without being T&M. */
	isTimeAndMaterials?: boolean;
	/** Inline validation errors. */
	errors?: Record<string, string>;
	/** Offer "+ Quick Add User" — under the add picker and inside its
	 *  dropdown. People created there join the crew as rows waiting for
	 *  hours. `companyId` is the work order's business (their default). */
	quickAdd?: QuickAddOptions;
	/** Keep the first row — the person the job is assigned to — as it is:
	 *  its person can't be changed or removed, though its hours can. Moving a
	 *  work order to someone else needs the Can Reassign Work Orders
	 *  permission, and removing row 0 would promote the next person. */
	primaryLocked?: boolean;
	/** Standard label CSS class. */
	labelClass: string;
	/** Optional extra classes for the outer wrapper. */
	className?: string;
}

export const TmAssigneeHoursFieldset: React.FC<TmAssigneeHoursFieldsetProps> = ({
	assigneeId,
	onAssigneeChange,
	additionalAssigneeIds,
	onAdditionalAssigneesChange,
	assigneeHours,
	onAssigneeHoursChange,
	bucketHours,
	isTimeAndMaterials = false,
	errors = {},
	quickAdd,
	primaryLocked = false,
	labelClass,
	className = '',
}) => {
	/** Display label cache keyed by user id. Hydrated for ids that are
	 *  already selected (e.g. on edit-mode mount) so we can show names
	 *  immediately without re-fetching during typeahead. */
	const [nameCache, setNameCache] = useState<Record<string, string>>({});

	useEffect(() => {
		/* Resolve any selected IDs that aren't in the cache yet. */
		const ids = [assigneeId, ...additionalAssigneeIds].filter(Boolean);
		const missing = ids.filter((id) => !(id in nameCache));
		if (missing.length === 0) return;
		let cancelled = false;
		(async () => {
			const entries = await Promise.all(
				missing.map(async (id) => {
					try {
						const res = await api.get<any>(`/users/${id}`);
						const u: DropdownUser | undefined = res.data?.data ?? res.data;
						if (u && u.id != null) return [id, displayLabel(u)] as const;
					} catch {
						/* Fall through to id-as-label below. */
					}
					return [id, id] as const;
				})
			);
			if (cancelled) return;
			setNameCache((prev) => {
				const next = { ...prev };
				for (const [id, name] of entries) next[id] = name;
				return next;
			});
		})();
		return () => { cancelled = true; };
	}, [assigneeId, additionalAssigneeIds, nameCache]);

	/** Search people (with workload context), excluding ones already assigned
	 *  so the picker can't produce a duplicate. Sends no company_id: the WO's
	 *  company is the CUSTOMER, while T&M crews are our own field staff, so
	 *  scoping on it hid nearly everyone. /users already applies the
	 *  requester's own company access scope; status=active keeps deactivated
	 *  people out, as on the other assignee pickers. */
	const searchUsers = useCallback(async (search: string): Promise<AsyncSearchableSelectOption[]> => {
		const params: Record<string, string> = { search, with_workload: '1', status: 'active' };
		const res = await api.get<any>('/users', params);
		const data: DropdownUser[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
		const exclude = new Set<string>([assigneeId, ...additionalAssigneeIds].filter(Boolean));
		return data
			.filter((u) => !exclude.has(String(u.id)))
			.map(userToOption);
	}, [assigneeId, additionalAssigneeIds]);

	/** Replace the user in a row with a different one. Picker exclusion
	 *  already prevents picking an already-assigned user. */
	const handleRowChange = useCallback((rowIndex: number, newId: string, opt: AsyncSearchableSelectOption | null) => {
		if (opt) {
			setNameCache((prev) => ({ ...prev, [opt.value]: opt.label }));
		}
		if (rowIndex === 0) {
			onAssigneeChange(newId);
		} else {
			const next = [...additionalAssigneeIds];
			next[rowIndex - 1] = newId;
			onAdditionalAssigneesChange(next);
		}
	}, [additionalAssigneeIds, onAssigneeChange, onAdditionalAssigneesChange]);

	/** Remove a row. Removing row 0 promotes the next assignee to primary
	 *  so the form never ends up with additional assignees but no primary. */
	const handleRemove = useCallback((rowIndex: number, userId: string) => {
		const nextHours = { ...assigneeHours };
		delete nextHours[userId];
		onAssigneeHoursChange(nextHours);
		if (rowIndex === 0) {
			if (additionalAssigneeIds.length > 0) {
				const [newPrimary, ...rest] = additionalAssigneeIds;
				onAssigneeChange(newPrimary);
				onAdditionalAssigneesChange(rest);
			} else {
				onAssigneeChange('');
			}
		} else {
			const next = additionalAssigneeIds.filter((_, i) => i !== rowIndex - 1);
			onAdditionalAssigneesChange(next);
		}
	}, [additionalAssigneeIds, assigneeHours, onAssigneeChange, onAdditionalAssigneesChange, onAssigneeHoursChange]);

	/** Append a row to additional assignees (or set as primary when the
	 *  form is empty). */
	const handleAddRow = useCallback((newId: string, opt: AsyncSearchableSelectOption | null) => {
		if (!newId) return;
		if (opt) setNameCache((prev) => ({ ...prev, [opt.value]: opt.label }));
		if (!assigneeId) {
			onAssigneeChange(newId);
		} else if (!additionalAssigneeIds.includes(newId) && newId !== assigneeId) {
			onAdditionalAssigneesChange([...additionalAssigneeIds, newId]);
		}
	}, [assigneeId, additionalAssigneeIds, onAssigneeChange, onAdditionalAssigneesChange]);

	/** Put freshly quick-added people on the crew as new rows, ready for
	 *  hours. The first one becomes primary when nobody is assigned yet;
	 *  everyone else is appended, skipping anyone already on the job. Their
	 *  names go straight into the cache so the rows don't flash an id. */
	const handleQuickAdded = useCallback((people: QuickAddedPersonRef[]) => {
		if (people.length === 0) return;
		setNameCache((prev) => {
			const next = { ...prev };
			for (const person of people) next[person.id] = person.name;
			return next;
		});
		const crew = addPeopleToCrew(assigneeId, additionalAssigneeIds, people);
		if (crew.primaryId !== assigneeId) onAssigneeChange(crew.primaryId);
		if (crew.additionalIds.length !== additionalAssigneeIds.length) onAdditionalAssigneesChange(crew.additionalIds);
	}, [assigneeId, additionalAssigneeIds, onAssigneeChange, onAdditionalAssigneesChange]);

	/* The same quick-add, offered from inside the "+ Add assignee" dropdown. */
	const quickAddFooterAction = useQuickAddFooterAction(quickAdd ? { defaultCompanyId: quickAdd.companyId, onCreated: handleQuickAdded } : null);

	const handleHoursChange = (userId: string, value: string) => {
		onAssigneeHoursChange({ ...assigneeHours, [userId]: value });
	};

	/* Build the full assignee list (primary + additional) for rendering. */
	const rows = [assigneeId, ...additionalAssigneeIds].filter(Boolean);
	const totalHours = rows.reduce((sum, uid) => {
		const n = Number(assigneeHours[uid]);
		return sum + (Number.isFinite(n) ? n : 0);
	}, 0);

	/* Bucket comparison. Only meaningful once a bucket has actually been
	   entered — on a non-T&M job it's optional, so an absent one simply
	   means "no ceiling to measure against", not zero. Over-allocation is
	   surfaced as a warning rather than a hard error: the office may
	   legitimately need to log more hours than were contracted, and the
	   ones that matter get settled at approval time. */
	const hasBucket = typeof bucketHours === 'number' && Number.isFinite(bucketHours) && bucketHours > 0;
	const isOverBucket = hasBucket && totalHours > (bucketHours as number);

	/* The form's error summary links every assignee problem to "#assigneeId"
	   — the id AssigneeFieldset puts on its primary picker. This variant has
	   no single picker to carry it, so the wrapper owns the anchor and, when
	   the summary link lands focus here, passes it on to the control that
	   actually needs attention: the first row still missing hours, or the
	   add picker when nobody is assigned yet. A click inside a child also
	   bubbles a focus event up to here; the target check leaves that alone. */
	const delegateFocus = (e: React.FocusEvent<HTMLDivElement>) => {
		if (e.target !== e.currentTarget) return;
		const missingUid = rows.find((uid) => !hasHours(assigneeHours[uid]));
		const target = missingUid
			? e.currentTarget.querySelector<HTMLElement>(`input[data-hours-for="${missingUid}"]`)
			: e.currentTarget.querySelector<HTMLElement>('#tm-assignee-add');
		if (!target) return;
		/* 'nearest' is a no-op when the row is already on screen, so a long
		   crew list can't push the offending input back out of view. */
		target.scrollIntoView({ block: 'nearest' });
		target.focus({ preventScroll: true });
	};

	return (
		<div
			id="assigneeId"
			role="group"
			aria-labelledby="assigneeId-label"
			tabIndex={-1}
			onFocus={delegateFocus}
			className={`tm-assignee-hours-fieldset focus:outline-none ${className}`}
			data-ref="molecule-tm-assignee-hours-fieldset"
		>
			<label id="assigneeId-label" className={labelClass}>
				Assignees &amp; Hours{isTimeAndMaterials ? ' (T&M)' : ''} <span className="text-red-500">*</span>
			</label>
			<p className="text-12px text-brand-text-muted mb-8px">
				Assign each employee and the number of hours they will bill against this work order.
				Every assignee needs hours before this work order can be submitted or completed.
			</p>

			{/* Editable rows */}
			{/* One person per row — who, then their hours. The row wraps by
			    its OWN width, not the viewport's: this editor sits in a page
			    card, in two different modals and in the field workflow's
			    panel, and on a 320px phone those leave it 190–240px. Below
			    roughly 360px the name takes the whole first line and the hours
			    group drops beneath it, so a name is never cut to a few letters
			    (the old single line left it 66px at 190 and 240, where only the
			    clear × still showed). Wider than that, everything sits on one
			    line. The dividers keep each person's two lines reading as one
			    unit. */}
			<div className="tm-assignee-hours-fieldset__rows divide-y divide-gray-200">
				{rows.length === 0 && (
					<div className="text-12px text-brand-text-muted italic">No assignees yet — pick one below.</div>
				)}
				{rows.map((uid, rowIndex) => {
					const lockedRow = rowIndex === 0 && primaryLocked;
					return (
					<div key={uid} className={`tm-assignee-hours-fieldset__row${rowIndex === 0 ? ' tm-assignee-hours-fieldset__row--primary' : ''} flex flex-wrap items-center gap-x-8px gap-y-6px py-8px first:pt-0`}>
						<div className="grow basis-176px min-w-0">
							<AsyncSearchableSelect
								id={`tm-assignee-${rowIndex}`}
								value={uid}
								selectedLabel={nameCache[uid] || ''}
								onChange={(id, opt) => handleRowChange(rowIndex, id, opt)}
								fetchOptions={searchUsers}
								placeholder="Select employee"
								disabled={lockedRow}
								/* The first row is the primary assignee. The
								   visual cue helps distinguish it from the
								   additional rows. The input's left corners go
								   square so they sit flush against the flat bar. */
								className={rowIndex === 0 ? 'border-l-4 border-brand-primary [&_input]:rounded-l-none' : ''}
							/>
						</div>
						<div className="tm-assignee-hours-fieldset__hours flex items-center gap-6px">
							<input
								type="number"
								/* The decimal keypad on phones — type=number
								   alone gets iOS's punctuation keyboard. Same
								   pairing as the sign-off Hours Worked list. */
								inputMode="decimal"
								min={0}
								step={0.25}
								value={assigneeHours[uid] ?? ''}
								onChange={(e) => handleHoursChange(uid, e.target.value)}
								placeholder="0"
								aria-label={`Hours for ${nameCache[uid] || uid}`}
								data-hours-for={uid}
								/* Once a submit has flagged missing hours, outline
								   the rows that are missing them — the summary
								   says "1 still missing", this says which one. */
								className={`w-76px px-10px py-10px border rounded-md text-16px desktop:text-14px text-brand-text text-right min-h-44px focus:outline-none focus:ring-2 ${errors.assigneeId && !hasHours(assigneeHours[uid]) ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-brand-primary focus:border-brand-primary'}`}
							/>
							{/* The unit, visible — on its own line under a name
							    the box needs to say what it holds. It also puts
							    a gap between the box and Remove. */}
							<span className="text-14px font-sans text-brand-text-muted" aria-hidden="true">hrs</span>
							{/* Inside the hours group, not beside it: flex-wrap
							   fills lines greedily, so a separate Remove landed
							   on a line of its own whenever it alone didn't fit.
							   Grouped, the hours and Remove always wrap together
							   — two lines per person on a phone, never three. */}
							{!lockedRow && (
							<button
								type="button"
								onClick={() => handleRemove(rowIndex, uid)}
								className="text-14px text-red-600 hover:text-red-800 min-h-44px inline-flex items-center justify-center px-6px"
								aria-label={`Remove assignee ${nameCache[uid] || uid}`}
							>
								Remove
							</button>
							)}
						</div>
					</div>
					);
				})}
			</div>

			{primaryLocked && rows.length > 0 && (
				<p className="tm-assignee-hours-fieldset__primary-note mt-4px text-12px font-sans text-brand-text-muted">Changing who this job is assigned to needs the Can Reassign Work Orders permission.</p>
			)}

			{/* Add row */}
			<div className="mt-12px">
				<AsyncSearchableSelect
					id="tm-assignee-add"
					value=""
					selectedLabel=""
					onChange={handleAddRow}
					fetchOptions={searchUsers}
					placeholder="+ Add assignee"
					footerAction={quickAddFooterAction}
				/>
				{/* Not in the list? Add them on the spot — see QuickAddUserLink. */}
				{quickAdd && <QuickAddUserLink defaultCompanyId={quickAdd.companyId} onCreated={handleQuickAdded} className="tm-assignee-hours-fieldset__quick-add mt-4px" />}
			</div>

			{/* Total — shown against the bucket when one exists, so the
			    remaining balance is readable without doing the arithmetic. */}
			<div className="tm-assignee-hours-fieldset__total mt-16px flex flex-wrap items-baseline justify-end gap-x-8px">
				<span className="text-14px font-semibold text-brand-text whitespace-nowrap">Total Hours:</span>
				<span className={`text-18px font-heading whitespace-nowrap ${isOverBucket ? 'text-red-600' : 'text-brand-primary'}`}>
					{totalHours}
					{hasBucket && <span className="text-14px text-brand-text-muted"> / {bucketHours}</span>}
				</span>
			</div>
			{hasBucket && (
				<p className={`mt-4px text-12px text-right ${isOverBucket ? 'text-red-600' : 'text-brand-text-muted'}`}>
					{isOverBucket
						? `${Number((totalHours - (bucketHours as number)).toFixed(2))} hrs over the bucket.`
						: `${Number(((bucketHours as number) - totalHours).toFixed(2))} hrs left in the bucket.`}
				</p>
			)}

			{errors?.assigneeId && (
				<p className="mt-8px text-14px text-red-600">{errors.assigneeId}</p>
			)}
		</div>
	);
};

export default TmAssigneeHoursFieldset;
